import { type ReactNode } from "react";
import { m, useReducedMotion } from "@/lib/motion";
import { springPresets } from "@/lib/motion";

interface PageTransitionProps {
  children: ReactNode;
}

/**
 * PageTransition — visibility-first wrapper.
 *
 * CRITICAL: Content MUST be visible by default. The animation is a pure
 * enhancement layered on top via `whileInView` / `animate`-only transitions.
 *
 * Historical context (RR-blank-screen):
 *   Previous versions used `initial={{ opacity: 0 }}` which caused entire
 *   pages (Onboarding, Dashboard, Profile) to render invisible whenever
 *   LazyMotion failed to hydrate in time, suspense flickered, or a route
 *   redirect interrupted the animation cycle. DOM was present but viewport
 *   was blank — breaking the candidate pipeline.
 *
 * Rule: never set `initial.opacity` to 0 here. Use a tiny y-offset only,
 * which degrades gracefully (content visible at final position if motion
 * never fires).
 */
export function PageTransition({ children }: PageTransitionProps) {
  const prefersReducedMotion = useReducedMotion();

  if (prefersReducedMotion) {
    return <div className="flex flex-1 flex-col">{children}</div>;
  }

  return (
    <m.div
      // initial.opacity intentionally 1 — content must paint immediately.
      initial={{ opacity: 1, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springPresets.smooth}
      className="flex flex-1 flex-col"
      style={{ opacity: 1 }}
    >
      {children}
    </m.div>
  );
}
